import { useEffect } from "react";
import { getIndustryRead, getIndustryReads } from "../../data/industryReads";
import { useLocale } from "../../i18n/LocaleContext";
import { buildLocalizedPath } from "../../lib/siteRouting";

type Props = {
  slug: string;
};

export function IndustryReadDetailPage({ slug }: Props) {
  const { locale } = useLocale();
  const article = getIndustryRead(locale, slug);
  const relatedReads = getIndustryReads(locale).filter((item) => item.slug !== slug).slice(0, 2);
  const detailPath = buildLocalizedPath(locale, `/industry/${slug}`);
  const isBeautyTech =
    article?.eyebrow.toLowerCase().includes("tech") ||
    article?.eyebrow.includes("科技") ||
    article?.eyebrow.includes("테크");
  const relatedLabel =
    locale === "zh"
      ? "更多行业观察"
      : locale === "ko"
        ? "추가 산업 관찰"
        : locale === "vi"
          ? "Thêm góc nhìn ngành"
          : "More Industry Reads";
  const sourceLabel =
    locale === "zh"
      ? "原文来源"
      : locale === "ko"
        ? "원문 출처"
        : locale === "vi"
          ? "Nguồn gốc"
          : "Original Source";
  const insightLabel =
    locale === "zh"
      ? "核心观察"
      : locale === "ko"
        ? "핵심 관찰"
        : locale === "vi"
          ? "Điểm chính"
          : "Key Signal";
  const signalLabel =
    locale === "zh"
      ? "观察类型"
      : locale === "ko"
        ? "리드 유형"
        : locale === "vi"
          ? "Loại góc nhìn"
          : "Read Type";

  useEffect(() => {
    if (!article) return;
    const pageTitle = `${article.title} | QIANDU`;
    const canonical = `https://www.qiandu.group${detailPath}`;

    const setMeta = (selector: string, value: string, attr = "content") => {
      const node = document.head.querySelector(selector);
      if (node) {
        node.setAttribute(attr, value);
      }
    };

    document.title = pageTitle;
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[name="twitter:title"]', pageTitle);
    setMeta('meta[name="description"]', article.summary);
    setMeta('meta[property="og:description"]', article.summary);
    setMeta('meta[name="twitter:description"]', article.summary);
    setMeta('meta[property="og:url"]', canonical);

    const canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonical);
    }
  }, [article, detailPath]);

  if (!article) {
    return (
      <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
        <div className="mx-auto max-w-5xl rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.86),rgba(240,246,249,0.94))] p-10 text-center shadow-[0_12px_24px_rgba(106,138,151,0.05)]">
          <a
            href={buildLocalizedPath(locale, "/")}
            className="inline-flex rounded-full border border-[#dce8ee] bg-white/80 px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64"
          >
            {locale === "zh" ? "返回首页" : locale === "ko" ? "홈으로 돌아가기" : locale === "vi" ? "Quay lại trang chủ" : "Back to Home"}
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
      <div className="mx-auto max-w-6xl">
        <a
          href={buildLocalizedPath(locale, "/")}
          className="inline-flex rounded-full border border-[#dce8ee] bg-[rgba(251,254,255,0.82)] px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64 shadow-[0_10px_22px_rgba(106,138,151,0.06)] backdrop-blur"
        >
          {locale === "zh" ? "返回首页" : locale === "ko" ? "홈으로 돌아가기" : locale === "vi" ? "Quay lại trang chủ" : "Back to Home"}
        </a>

        <div
          className={`mt-8 overflow-hidden rounded-[2.8rem] border p-6 shadow-[0_14px_26px_rgba(106,138,151,0.06)] sm:p-8 ${
            isBeautyTech
              ? "border-[#d4e4ea] bg-[radial-gradient(circle_at_top_left,rgba(139,208,232,0.24),transparent_34%),linear-gradient(180deg,rgba(244,251,254,0.96),rgba(231,242,247,0.96))]"
              : "border-[#dce8ee] bg-[radial-gradient(circle_at_top_left,rgba(196,218,227,0.22),transparent_30%),linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))]"
          }`}
        >
          <div className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr] lg:items-start">
            <div>
              <div
                className={`inline-flex rounded-full px-4 py-2 text-[8px] uppercase tracking-[0.4em] ${
                  isBeautyTech
                    ? "bg-[rgba(227,244,250,0.95)] text-[#5f8ea0]"
                    : "bg-[rgba(239,248,251,0.9)] text-aurora/80"
                }`}
              >
                {article.eyebrow}
              </div>
              <h1 className="mt-5 max-w-[48rem] font-display text-[2rem] leading-[1.04] text-[#1f2b31] sm:text-[3rem]">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-soft/44">
                <span>{article.source}</span>
                <span>{article.date}</span>
              </div>
              <p className="mt-6 max-w-[42rem] text-[0.94rem] leading-8 text-soft/60">{article.intro}</p>
              <a
                href={article.url}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex rounded-full bg-[linear-gradient(135deg,#88aebb,#9fc8d2)] px-6 py-3.5 text-[9px] uppercase tracking-[0.36em] text-white shadow-[0_10px_20px_rgba(123,167,184,0.16)]"
              >
                {sourceLabel}
              </a>
            </div>

            <div className="grid gap-4">
              <div
                className={`relative overflow-hidden rounded-[2.2rem] border border-white/55 p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] backdrop-blur ${
                  isBeautyTech
                    ? "bg-[linear-gradient(180deg,rgba(233,247,252,0.94),rgba(215,235,243,0.94))]"
                    : "bg-[linear-gradient(180deg,rgba(248,252,254,0.96),rgba(232,241,246,0.94))]"
                }`}
              >
                <div
                  className={`absolute -right-8 -top-10 h-36 w-36 rounded-full blur-2xl ${
                    isBeautyTech ? "bg-[rgba(121,194,221,0.34)]" : "bg-[rgba(173,196,208,0.28)]"
                  }`}
                />
                <div
                  className={`absolute -bottom-10 left-8 h-28 w-28 rounded-full blur-2xl ${
                    isBeautyTech ? "bg-[rgba(159,210,229,0.28)]" : "bg-[rgba(199,216,225,0.28)]"
                  }`}
                />
                <div className="relative">
                  <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{article.eyebrow}</div>
                  <div className="mt-5 grid grid-cols-[1.1fr_0.9fr] gap-3">
                    <div className="rounded-[1.6rem] border border-white/55 bg-white/58 p-4">
                      <div className="h-24 rounded-[1.2rem] bg-[linear-gradient(135deg,rgba(255,255,255,0.9),rgba(225,239,245,0.82))]" />
                      <div className="mt-4 h-[1px] w-full bg-[rgba(118,150,163,0.18)]" />
                      <div className="mt-4 space-y-2">
                        <div className="h-2 w-16 rounded-full bg-[rgba(118,150,163,0.24)]" />
                        <div className="h-2 w-24 rounded-full bg-[rgba(118,150,163,0.18)]" />
                        <div className="h-2 w-20 rounded-full bg-[rgba(118,150,163,0.14)]" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-[1.4rem] border border-white/55 bg-white/62 p-4">
                        <div className="h-10 rounded-[1rem] bg-[linear-gradient(135deg,rgba(255,255,255,0.88),rgba(214,231,239,0.82))]" />
                        <div className="mt-3 h-2 w-12 rounded-full bg-[rgba(118,150,163,0.24)]" />
                      </div>
                      <div className="rounded-[1.4rem] border border-white/55 bg-white/62 p-4">
                        <div className="flex items-end gap-2">
                          <div className="h-10 w-8 rounded-t-full bg-[rgba(134,177,194,0.2)]" />
                          <div className="h-16 w-8 rounded-t-full bg-[rgba(134,177,194,0.32)]" />
                          <div className="h-12 w-8 rounded-t-full bg-[rgba(134,177,194,0.24)]" />
                        </div>
                        <div className="mt-3 h-2 w-16 rounded-full bg-[rgba(118,150,163,0.18)]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-[2rem] border border-white/55 bg-white/72 p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] backdrop-blur">
                <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{signalLabel}</div>
                <div className="mt-3 font-display text-[1.18rem] leading-tight text-[#1f2b31]">
                  {article.eyebrow}
                </div>
              </div>
              <div className="rounded-[2rem] border border-white/55 bg-white/72 p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] backdrop-blur">
                <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{insightLabel}</div>
                <p className="mt-3 text-[0.86rem] leading-6 text-soft/58">{article.summary}</p>
              </div>
            </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          <h2 className="sr-only">{insightLabel}</h2>
          {article.sections.map((section) => (
            <article
              key={section.title}
              className="rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-6 shadow-[0_10px_20px_rgba(106,138,151,0.05)]"
            >
              <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{section.title}</div>
              <p className="mt-4 text-[0.9rem] leading-7 text-soft/60">{section.body}</p>
            </article>
          ))}
        </div>

        {relatedReads.length ? (
          <div className="mt-12 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(240,246,249,0.92))] p-5 shadow-[0_12px_24px_rgba(106,138,151,0.05)] sm:p-6">
            <div className="text-[8px] uppercase tracking-[0.4em] text-aurora/72">{relatedLabel}</div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {relatedReads.map((item) => (
                <a
                  key={item.slug}
                  href={buildLocalizedPath(locale, `/industry/${item.slug}`)}
                  className="rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))] p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] transition hover:-translate-y-1 hover:shadow-[0_14px_24px_rgba(106,138,151,0.07)]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="inline-flex rounded-full bg-[rgba(239,248,251,0.82)] px-3 py-1 text-[8px] uppercase tracking-[0.3em] text-[#6f8d99]">
                      {item.source}
                    </div>
                    <div className="text-[8px] uppercase tracking-[0.3em] text-soft/44">{item.date}</div>
                  </div>
                  <h3 className="mt-4 font-display text-[1.12rem] leading-tight text-[#1f2b31]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[0.84rem] leading-6 text-soft/58">{item.summary}</p>
                </a>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
